/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {decimalDigest} from '../digest';
import * as i18n from '../i18n_ast';

import {Serializer} from './serializer';
import * as xml from './xml_helper';

const /** @type {?} */ _MESSAGES_TAG = 'messagebundle';
const /** @type {?} */ _MESSAGE_TAG = 'msg';
const /** @type {?} */ _PLACEHOLDER_TAG = 'ph';
const /** @type {?} */ _EXEMPLE_TAG = 'ex';

const /** @type {?} */ _DOCTYPE = `<!ELEMENT messagebundle (msg)*>
<!ATTLIST messagebundle class CDATA #IMPLIED>

<!ELEMENT msg (#PCDATA|ph|source)*>
<!ATTLIST msg id CDATA #IMPLIED>
<!ATTLIST msg seq CDATA #IMPLIED>
<!ATTLIST msg name CDATA #IMPLIED>
<!ATTLIST msg desc CDATA #IMPLIED>
<!ATTLIST msg meaning CDATA #IMPLIED>
<!ATTLIST msg obsolete (obsolete) #IMPLIED>
<!ATTLIST msg xml:space (default|preserve) "default">
<!ATTLIST msg is_hidden CDATA #IMPLIED>

<!ELEMENT source (#PCDATA)>

<!ELEMENT ph (#PCDATA|ex)*>
<!ATTLIST ph name CDATA #REQUIRED>

<!ELEMENT ex (#PCDATA)>`;

export class Xmb implements Serializer {
/**
 * @param {?} messages
 * @return {?}
 */
write(messages: i18n.Message[]): string {
    const /** @type {?} */ exampleVisitor = new ExampleVisitor();
    const /** @type {?} */ visitor = new _Visitor();
    const /** @type {?} */ visited: {[id: string]: boolean} = {};
    let /** @type {?} */ rootNode = new xml.Tag(_MESSAGES_TAG);

    messages.forEach(message => {
      const /** @type {?} */ id = this.digest(message);

      // deduplicate messages
      if (visited[id]) return;
      visited[id] = true;

      const /** @type {?} */ attrs: {[k: string]: string} = {id};

      if (message.description) {
        attrs['desc'] = message.description;
      }

      if (message.meaning) {
        attrs['meaning'] = message.meaning;
      }

      rootNode.children.push(
          new xml.CR(2), new xml.Tag(_MESSAGE_TAG, attrs, visitor.serialize(message.nodes)));
    });

    rootNode.children.push(new xml.CR());

    return xml.serialize([
      new xml.Declaration({version: '1.0', encoding: 'UTF-8'}),
      new xml.CR(),
      new xml.Doctype(_MESSAGES_TAG, _DOCTYPE),
      new xml.CR(),
      exampleVisitor.addDefaultExamples(rootNode),
      new xml.CR(),
    ]);
  }
/**
 * @param {?} content
 * @param {?} url
 * @return {?}
 */
load(content: string, url: string): {[msgId: string]: i18n.Node[]} {
    throw new Error('Unsupported');
  }
/**
 * @param {?} message
 * @return {?}
 */
digest(message: i18n.Message): string { return digest(message); }
}

class _Visitor implements i18n.Visitor {
/**
 * @param {?} text
 * @param {?=} context
 * @return {?}
 */
visitText(text: i18n.Text, context?: any): xml.Node[] { return [new xml.Text(text.value)]; }
/**
 * @param {?} container
 * @param {?=} context
 * @return {?}
 */
visitContainer(container: i18n.Container, context?: any): xml.Node[] {
    const /** @type {?} */ nodes: xml.Node[] = [];
    container.children.forEach((node: i18n.Node) => nodes.push(...node.visit(this)));
    return nodes;
  }
/**
 * @param {?} icu
 * @param {?=} context
 * @return {?}
 */
visitIcu(icu: i18n.Icu, context?: any): xml.Node[] {
    const /** @type {?} */ nodes = [new xml.Text(`{${icu.expressionPlaceholder}, ${icu.type}, `)];

    Object.keys(icu.cases).forEach((c: string) => {
      nodes.push(new xml.Text(`${c} {`), ...icu.cases[c].visit(this), new xml.Text(`} `));
    });

    nodes.push(new xml.Text(`}`));

    return nodes;
  }
/**
 * @param {?} ph
 * @param {?=} context
 * @return {?}
 */
visitTagPlaceholder(ph: i18n.TagPlaceholder, context?: any): xml.Node[] {
    const /** @type {?} */ startEx = new xml.Tag(_EXEMPLE_TAG, {}, [new xml.Text(`<${ph.tag}>`)]);
    const /** @type {?} */ startTagPh = new xml.Tag(_PLACEHOLDER_TAG, {name: ph.startName}, [startEx]);
    if (ph.isVoid) {
      // void tags have no children nor closing tags
      return [startTagPh];
    }

    const /** @type {?} */ closeEx = new xml.Tag(_EXEMPLE_TAG, {}, [new xml.Text(`</${ph.tag}>`)]);
    const /** @type {?} */ closeTagPh = new xml.Tag(_PLACEHOLDER_TAG, {name: ph.closeName}, [closeEx]);

    return [startTagPh, ...this.serialize(ph.children), closeTagPh];
  }
/**
 * @param {?} ph
 * @param {?=} context
 * @return {?}
 */
visitPlaceholder(ph: i18n.Placeholder, context?: any): xml.Node[] {
    return [new xml.Tag(_PLACEHOLDER_TAG, {name: ph.name})];
  }
/**
 * @param {?} ph
 * @param {?=} context
 * @return {?}
 */
visitIcuPlaceholder(ph: i18n.IcuPlaceholder, context?: any): xml.Node[] {
    return [new xml.Tag(_PLACEHOLDER_TAG, {name: ph.name})];
  }
/**
 * @param {?} nodes
 * @return {?}
 */
serialize(nodes: i18n.Node[]): xml.Node[] {
    return [].concat(...nodes.map(node => node.visit(this)));
  }
}
/**
 * @param {?} message
 * @return {?}
 */
export function digest(message: i18n.Message): string {
  return decimalDigest(message);
}

// TC requires at least one non-empty example on placeholders
class ExampleVisitor implements xml.IVisitor {
/**
 * @param {?} node
 * @return {?}
 */
addDefaultExamples(node: xml.Node): xml.Node {
    node.visit(this);
    return node;
  }
/**
 * @param {?} tag
 * @return {?}
 */
visitTag(tag: xml.Tag): void {
    if (tag.name === _PLACEHOLDER_TAG) {
      if (!tag.children || tag.children.length == 0) {
        const /** @type {?} */ exText = new xml.Text(tag.attrs['name'] || '...');
        tag.children = [new xml.Tag(_EXEMPLE_TAG, {}, [exText])];
      }
    } else if (tag.children) {
      tag.children.forEach(node => node.visit(this));
    }
  }
/**
 * @param {?} text
 * @return {?}
 */
visitText(text: xml.Text): void {}
/**
 * @param {?} decl
 * @return {?}
 */
visitDeclaration(decl: xml.Declaration): void {}
/**
 * @param {?} doctype
 * @return {?}
 */
visitDoctype(doctype: xml.Doctype): void {}
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
